import { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash-es/debounce';

import type { DebounceSettings } from 'lodash-es';

/**
 * @description 基于 lodash-es 的防抖 Hook，返回稳定的防抖函数
 * @param func 需要防抖的函数
 * @param waitTime 防抖等待时间（毫秒）
 * @param options 透传给 lodash-es debounce 的配置
 *
 * @example
 * ```tsx
 * // 示例 1：搜索框输入防抖（延迟执行）
 * function SearchComponent() {
 *   const [keyword, setKeyword] = useState('');
 *
 *   const handleSearch = useDebounce(
 *     (value: string) => {
 *       console.log('搜索:', value);
 *       // 调用搜索 API
 *     },
 *     500,
 *     { leading: false, trailing: true }
 *   );
 *
 *   return (
 *     <Input
 *       value={keyword}
 *       onChange={(e) => {
 *         setKeyword(e.target.value);
 *         handleSearch(e.target.value);
 *       }}
 *       placeholder="输入关键词搜索"
 *     />
 *   );
 * }
 *
 * // 示例 2：保存按钮防抖（立即执行）
 * function SaveButton() {
 *   const handleSave = useDebounce(
 *     async (data: any) => {
 *       console.log('保存中...');
 *       await saveData(data);
 *       message.success('保存成功');
 *     },
 *     1000,
 *     { leading: true, trailing: false }
 *   );
 *
 *   return (
 *     <Button onClick={() => handleSave({ name: '测试数据' })}>
 *       保存
 *     </Button>
 *   );
 * }
 * ```
 */

export function useDebounce<T extends (...args: any[]) => any>(
    func: T,
    waitTime: number,
    options?: DebounceSettings
): (...args: Parameters<T>) => void {
    const latestFuncRef = useRef(func);

    useEffect(() => {
        latestFuncRef.current = func;
    }, [func]);

    const debounceOptions = useMemo<DebounceSettings | undefined>(() => {
        if (!options) {
            return undefined;
        }

        return {
            leading: options.leading,
            trailing: options.trailing,
            maxWait: options.maxWait,
        };
    }, [options?.leading, options?.trailing, options?.maxWait]);

    const debouncedFn = useMemo(() => {
        const handler = debounce((...args: Parameters<T>) => latestFuncRef.current(...args), waitTime, debounceOptions);
        return handler;
    }, [waitTime, debounceOptions]);

    useEffect(
        () => () => {
            debouncedFn.cancel();
        },
        [debouncedFn]
    );

    return useCallback((...args: Parameters<T>) => debouncedFn(...args), [debouncedFn]);
}
